import { useMutation } from "@tanstack/react-query";
import type { Web3Modal } from "@web3modal/wagmi/dist/types/src/client";
import { createWeb3Modal } from "@web3modal/wagmi/react";
import { defaultWagmiConfig } from "@web3modal/wagmi/react/config";
import WalletConfigAPI from "api/WalletConfigAPI";
import {
  getAppMode,
  getCurrentApplication,
} from "ee/selectors/applicationSelectors";
import { APP_MODE } from "entities/App";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { Config } from "wagmi";
import {
  mainnet,
  sepolia,
  initVerse,
  initVerseGenesis,
  type Chain,
} from "wagmi/chains";
import * as chains from "wagmi/chains";

const projectId = "c983403324f5afade6c5e4d8d2a885a6";
const metadata = {
  name: "Appsmith",
  description: "Appsmith",
  url: "www.appsmith.com",
  icons: [],
};

export const DEFAULT_CHAINS: readonly [Chain, ...Chain[]] = [
  mainnet,
  sepolia,
  initVerse,
  initVerseGenesis,
] as const;

export const DEFAULT_WAGMI_CONFIG = {
  projectId,
  metadata,
  batch: {
    multicall: true,
  },
  auth: {
    email: false,
  },
  enableCoinbase: false,
  enableInjected: true,
  enableEIP6963: true,
};

export const DEFAULT_WEB3_MODAL_CONFIG = {
  projectId,
  metadata,
  isSiweEnabled: false,
  enableAnalytics: false,
  enableOnramp: false,
  enableSwaps: false,
  allowUnsupportedChain: false,
  themeMode: "dark" as const,
};

let INITIALED = false;
let IS_INITIALIZING = false;
export const ALL_CHAINS: Chain[] = Object.values(chains);

export function useWagmiConfig() {
  const currentAppMode = useSelector(getAppMode);
  const currentApplication = useSelector(getCurrentApplication);
  const { mutateAsync } = useMutation({
    mutationFn: WalletConfigAPI.getSupportedChains,
  });
  const [wagmiConfig, setWagmiConfig] = useState<Config>();
  const [web3Modal, setWeb3Modal] = useState<Web3Modal>();

  const init = useCallback(async () => {
    if (INITIALED || IS_INITIALIZING) return;
    if (
      location.href.includes("/app/") &&
      (!currentAppMode ||
        (currentAppMode === APP_MODE.PUBLISHED && !currentApplication?.id))
    )
      return;
    let chains: readonly [Chain, ...Chain[]] = DEFAULT_CHAINS;
    if (currentAppMode === APP_MODE.PUBLISHED && currentApplication?.id) {
      const currentSupportedChains =
        (await mutateAsync(currentApplication?.id)) || [];
      chains = !!currentSupportedChains?.length
        ? (ALL_CHAINS.filter((chain) =>
            currentSupportedChains.includes(chain.id),
          ) as unknown as readonly [Chain, ...Chain[]])
        : DEFAULT_CHAINS;
    }
    const _wagmiConfig = defaultWagmiConfig({
      ...DEFAULT_WAGMI_CONFIG,
      chains,
    });
    (window as any).WAGMI_CONFIG = _wagmiConfig;
    setWagmiConfig(_wagmiConfig);

    const web3Modal = createWeb3Modal({
      ...DEFAULT_WEB3_MODAL_CONFIG,
      wagmiConfig: _wagmiConfig,
    });
    (window as any).WEB3_MODAL = web3Modal;
    setWeb3Modal(web3Modal);
    INITIALED = true;
  }, [currentAppMode, currentApplication?.id, mutateAsync]);

  useEffect(() => {
    init();
  }, [init]);

  return { wagmiConfig, web3Modal };
}
